/**
 * SOC-011 (auditoria SOC 2 #2, ALTO) — o retrato do chat era gravado sob UMA
 * chave global de `localStorage`, sem `tenantId` nem `userId`. Dois usuários
 * de inquilinos diferentes no MESMO navegador (quiosque de fábrica, tablet
 * de turno) e o segundo abria o retrato de conversa do primeiro.
 *
 * Extraído de `chatStore.ts` porque o store já beira o teto de 500 linhas da
 * catraca de modularidade (`lint:strict`) — mesmo motivo de `chatStore.rename.ts`.
 * `useChatStore` é importado de volta (`./chatStore`) só para uso DENTRO de
 * `syncChatStoreIdentity`, nunca no topo do módulo — a dependência circular
 * é segura porque essa função só roda depois que os dois módulos já
 * terminaram de carregar.
 */
import type { StateStorage } from 'zustand/middleware'
import { useAuthStore } from '../shared/stores/auth'
import { useChatStore, type ChatState } from './chatStore'

/**
 * Nome-base da chave de disco. Sozinho, sem sufixo, é o balde GLOBAL que
 * vazava o retrato de um inquilino para o próximo usuário do MESMO
 * navegador: depois deste conserto ele não tem mais dono, só resíduo de quem
 * já usou o produto antes dele. Nunca migrado — apagado em
 * `syncChatStoreIdentity`, porque o dono é desconhecido por construção.
 */
export const CHAT_STORAGE_NAME = 'axiomgo-chat-storage'

/**
 * Chave real de disco: por INQUILINO + USUÁRIO. `null` quando `userId` ainda
 * não é conhecido — nesse caso `identityScopedStorage` não lê nem grava nada
 * (melhor perder cache anônimo que servir cache alheio).
 */
export function chatStorageKeyFor(tenantId: string, userId: string): string | null {
  if (!userId) return null
  return `${CHAT_STORAGE_NAME}::${tenantId}::${userId}`
}

/**
 * Campos de DADO da loja (sem as ações) — a forma do "zerar a RAM" usada na
 * troca de identidade (`syncChatStoreIdentity`) e na saída
 * (`clearLocalIdentityData`, em `src/lib/identity-storage.ts`). Chave nova
 * com estado antigo em RAM é o mesmo vazamento do SOC-011 com outro nome.
 */
export function createInitialChatState(): Pick<
  ChatState,
  | 'conversations'
  | 'activeConversationId'
  | 'messages'
  | 'isTyping'
  | 'pendingConversation'
  | 'progressStep'
  | 'progressDetail'
  | 'streamError'
  | 'inactivityNotice'
  | 'inactivityResuming'
  | 'inputDraft'
> {
  return {
    conversations: [],
    activeConversationId: null,
    messages: [],
    isTyping: false,
    pendingConversation: false,
    progressStep: null,
    progressDetail: null,
    streamError: null,
    inactivityNotice: null,
    inactivityResuming: false,
    inputDraft: '',
  }
}

/**
 * A escrita automática do `persist` só é liberada depois que a identidade
 * atual termina de re-hidratar. Sem isto,
 * `useChatStore.setState(...)` (que o `persist` grava a cada chamada,
 * sempre) gravaria o estado VAZIO na chave da identidade-ALVO ANTES do
 * `rehydrate` ler o que já existe lá — apagando o retrato real de quem está
 * entrando antes de a leitura acontecer.
 */
let geracaoDaHidratacao = 0
let identidadeChatHidratada: string | null = null
/**
 * O `userId` da última identidade hidratada. É o que separa uma TROCA real de
 * identidade (outro usuário no mesmo navegador — o vazamento do SOC-011) de
 * uma RESOLUÇÃO de tenant do MESMO usuário (placeholder do Clerk `user.id` →
 * UUID real devolvido por `/auth/status`). No primeiro caso a RAM precisa ser
 * zerada; no segundo, zerar apaga a conversa que já carregou pela URL.
 */
let identidadeChatUsuarioHidratado: string | null = null

/**
 * A armadilha do conserto: o `name` do `persist` do zustand é fixo na
 * criação da loja, e a hidratação rodaria no import do módulo — ANTES do
 * `syncClerkState` (o Clerk resolve a identidade depois, de forma
 * assíncrona). Em vez de um nome fixo, este adaptador CALCULA a chave em
 * cada `getItem`/`setItem`/`removeItem` lendo `useAuthStore.getState()` no
 * momento da chamada — a mesma loja atende qualquer identidade sem precisar
 * recriar o `persist`. `skipHydration: true` (em `chatStore.ts`) evita a
 * hidratação anônima no import; `syncChatStoreIdentity` dispara a
 * hidratação real assim que `(tenantId, userId)` fica conhecido.
 */
export const identityScopedStorage: StateStorage = {
  getItem: () => {
    const { tenantId, userId } = useAuthStore.getState()
    const key = chatStorageKeyFor(tenantId, userId)
    return key ? localStorage.getItem(key) : null
  },
  setItem: (_name, value) => {
    if (identidadeChatHidratada === null) return
    const { tenantId, userId } = useAuthStore.getState()
    const key = chatStorageKeyFor(tenantId, userId)
    if (key && key === identidadeChatHidratada) localStorage.setItem(key, value)
  },
  removeItem: () => {
    const { tenantId, userId } = useAuthStore.getState()
    const key = chatStorageKeyFor(tenantId, userId)
    if (key) localStorage.removeItem(key)
  },
}

/**
 * Chaveia o retrato do chat pela identidade atual e re-hidrata. Chamado
 * pelo `AuthGuard` assim que o Clerk resolve `(tenantId, userId)`.
 *
 * Troca de identidade no MESMO navegador (outro `userId`, ou o primeiro boot
 * da sessão) reseta a RAM antes de hidratar a próxima: chave nova com o estado
 * antigo ainda em memória é o mesmo vazamento do SOC-011 com outro nome.
 *
 * Resolução de tenant do MESMO `userId` (placeholder do Clerk → UUID real)
 * NÃO reseta a RAM NEM re-hidrata: é a mesma pessoa, só a chave de disco
 * re-aponta. Re-hidratar aqui leria o retrato obsoleto de uma sessão anterior
 * do mesmo inquilino e sobrescreveria a conversa que acabou de carregar pela
 * URL — o cabeçalho cai em "Nova conversa" (STO-2170).
 *
 * Sem `userId` (identidade incompleta), `chatStorageKeyFor` devolve `null` e
 * esta função só zera a RAM — não hidrata nem grava nada (melhor perder
 * cache anônimo que servir cache alheio).
 */
export async function syncChatStoreIdentity(tenantId: string, userId: string): Promise<void> {
  const key = chatStorageKeyFor(tenantId, userId)
  if (key === identidadeChatHidratada) return

  const geracao = ++geracaoDaHidratacao
  const trocaDeUsuario = userId !== identidadeChatUsuarioHidratado
  identidadeChatUsuarioHidratado = userId

  if (trocaDeUsuario) {
    // Troca REAL de identidade (outro usuário, ou o primeiro boot): zera a RAM
    // antes de hidratar a próxima. Chave nova com o estado antigo em RAM é o
    // mesmo vazamento do SOC-011 com outro nome.
    identidadeChatHidratada = null
    useChatStore.setState(createInitialChatState())
    if (!key) return

    // Resíduo de todo navegador que já usou o produto antes deste conserto:
    // dono desconhecido por construção, então apaga em vez de migrar.
    localStorage.removeItem(CHAT_STORAGE_NAME)
    try {
      await useChatStore.persist.rehydrate()
      if (geracao !== geracaoDaHidratacao) await useChatStore.persist.rehydrate()
    } finally {
      if (geracao === geracaoDaHidratacao) identidadeChatHidratada = key
    }
    return
  }

  // Resolução de tenant do MESMO usuário (placeholder do Clerk → UUID real):
  // re-aponta a chave de disco SEM zerar a RAM e SEM re-hidratar. Re-hidratar
  // aqui leria o retrato OBSOLETO gravado por uma sessão anterior do mesmo
  // inquilino (activeConversationId null / conversas vazias) e sobrescreveria a
  // conversa que acabou de carregar pela URL — o cabeçalho cai em "Nova
  // conversa" (STO-2170). A RAM já tem o estado certo; as próximas escritas
  // (`setConversations`, `setInactivityNotice`, ...) persistem sob a chave real.
  identidadeChatHidratada = key
  localStorage.removeItem(CHAT_STORAGE_NAME)
}
